import { Feature, FeatureCollection, Geometry, LineString, Position } from "geojson";
import { IconLayer, PickingInfo } from "deck.gl";
import { isEmpty, isJson, isNil } from "./utils";

import { CurbZone } from "@/types/curb-zone.type";
import { IconStation } from "@/types";
import { MARKER_MAPPING } from "@/modules/offstreet-parking/off-street-parking.const";
import { cloneDeep } from "lodash";
import { findObject } from "./array";
import { getPlace } from "./format-data";

export const copyLineString = async (input: Feature<LineString, CurbZone>) => {
  const copiedCurbZone = cloneDeep(input);
  const newCoordinates = copiedCurbZone.geometry.coordinates.map((item) => [item[0] + 0.00002, item[1] - 0.00005]);
  copiedCurbZone.geometry = {
    ...copiedCurbZone.geometry,
    coordinates: newCoordinates,
  };
  copiedCurbZone.properties.geometry.coordinates = copiedCurbZone.properties.geometry.coordinates.map(
    (item: number[][]) => {
      return item.map((point) => [point[0] + 0.00002, point[1] - 0.00005]);
    },
  );
  const convertCurbZone = await getPlace(copiedCurbZone);
  return {
    ...convertCurbZone,
    id: undefined,
    properties: {
      ...convertCurbZone.properties,
      id: undefined,
      curbZoneId: undefined,
    },
  };
};

export const createMarkerLayer = (point: { coordinates: [number, number] }) => {
  return new IconLayer({
    id: "icon-layer",
    data: [point],
    pickable: true,
    iconAtlas: "images/maker.png",
    iconMapping: MARKER_MAPPING,
    getIcon: () => "marker",
    sizeScale: 8,
    getPosition: (d: IconStation) => d.coordinates,
    getSize: () => 3,
  });
};

export const filterFeatureIndex = <T extends { id?: string | number }>(
  editionData: FeatureCollection,
  selectedFeature?: T | T[],
) => {
  if (isEmpty(selectedFeature) || isNil(selectedFeature)) return [];
  const indexes: number[] = [];
  if (Array.isArray(selectedFeature)) {
    selectedFeature.forEach((item) => {
      if (!isNil(item) && item.id) {
        const fIndex = findObject(editionData.features, "id", item.id);
        if (fIndex > -1) {
          indexes.push(fIndex);
        }
      }
    });
  } else if (selectedFeature && selectedFeature.id) {
    const fIndex = findObject(editionData.features, "id", selectedFeature.id);
    if (fIndex > -1) {
      indexes.push(fIndex);
    }
  }
  return indexes;
};

export const filterLineStringFromPoint = (infos?: PickingInfo[]) => {
  if (!infos?.length) return [];

  return infos.reduce((filteredArray: PickingInfo["object"][], info: PickingInfo) => {
    if (info?.object?.id && info.object.geometry?.type === "LineString") {
      filteredArray.push(info.object);
    }
    return filteredArray;
  }, []);
};

/**
 * Parses a GeoJSON geometry from a raw JSON string, returning null when the string
 * is not valid JSON or does not describe a geometry.
 */
export const parseBoundaryGeometry = (raw: string): Geometry | null => {
  if (!isJson(raw)) return null;
  const parsed = JSON.parse(raw);
  return parsed && typeof parsed === "object" && "type" in parsed ? (parsed as Geometry) : null;
};

/**
 * Serializes a GeoJSON geometry to its WKT (Well-Known Text) representation,
 * emitting coordinates as "longitude latitude" pairs.
 */
export const geometryToWkt = (geometry: Geometry): string => {
  const point = (position: Position) => `${position[0]} ${position[1]}`;
  const ring = (positions: Position[]) => `(${positions.map(point).join(",")})`;
  const polygon = (rings: Position[][]) => `(${rings.map(ring).join(",")})`;

  switch (geometry.type) {
    case "Point":
      return `POINT(${point(geometry.coordinates)})`;
    case "MultiPoint":
      return `MULTIPOINT(${geometry.coordinates.map(point).join(",")})`;
    case "LineString":
      return `LINESTRING(${geometry.coordinates.map(point).join(",")})`;
    case "MultiLineString":
      return `MULTILINESTRING(${geometry.coordinates.map(ring).join(",")})`;
    case "Polygon":
      return `POLYGON(${geometry.coordinates.map(ring).join(",")})`;
    case "MultiPolygon":
      return `MULTIPOLYGON(${geometry.coordinates.map(polygon).join(",")})`;
    case "GeometryCollection":
      return `GEOMETRYCOLLECTION(${geometry.geometries.map(geometryToWkt).join(",")})`;
  }
};
